import { useEffect, useState } from "react";
import { useAuth } from "../context/auth";
import { isProductionMode } from "../util/util";

const firebase = window.firebase;
const firestore = firebase.firestore();

export const getUser = async (userId) => {
    const snapshot = await firebase
        .database()
        .ref(`/users/${userId}`)
        .once("value");

    const value = snapshot.val();

    if (value) {
        return {
            id: userId,
            ...value,
        };
    } else {
        return null;
    }
};

export const useSharedLists = () => {
    const authState = useAuth();
    const [sharedLists, setSharedLists] = useState(null);

    useEffect(() => {
        if (authState && authState.user) {
            const unsubscribe = firestore
                .collection("sharedLists")
                .where("members", "array-contains", authState.user.uid)
                .onSnapshot((querySnapshot) => {
                    const docs = [];
                    querySnapshot.forEach((doc) =>
                        docs.push({ id: doc.id, ...doc.data() })
                    );
                    setSharedLists(docs);
                });

            return () => unsubscribe();
        } else {
            setSharedLists(null);
        }
    }, [authState]);

    return sharedLists;
};

export const addMovieToSharedList = async (sharedList, movie, userId) => {
    const batch = firestore.batch();
    batch.set(
        firestore
            .collection("sharedLists")
            .doc(sharedList.id)
            .collection("items")
            .doc(),
        {
            itemType: "movie",
            traktId: movie.ids.trakt,
            addedBy: userId,
            addedOn: firebase.firestore.FieldValue.serverTimestamp(),
        }
    );

    batch.set(firestore.collection("conversationMessages").doc(), {
        type: "shared_list_item_added",
        sender: userId,
        createdOn: firebase.firestore.FieldValue.serverTimestamp(),
        conversationId: sharedList.conversationId,
        parentId: sharedList.id,
        sharedListName: sharedList.name,
        itemType: "movie",
        traktId: movie.ids.trakt,
        itemTitle: movie.title,
    });

    await batch.commit();
};

export const removeMovieFromSharedList = async (sharedList, movie, userId) => {
    const snapshot = await firestore
        .collection("sharedLists")
        .doc(sharedList.id)
        .collection("items")
        .where("traktId", "==", movie.ids.trakt)
        .get();

    const docs = [];
    snapshot.forEach((doc) => docs.push(doc));

    if (docs.length === 0) {
        return;
    }

    const batch = firestore.batch();

    batch.set(firestore.collection("conversationMessages").doc(), {
        type: "shared_list_item_removed",
        sender: userId,
        createdOn: firebase.firestore.FieldValue.serverTimestamp(),
        conversationId: sharedList.conversationId,
        parentId: sharedList.id,
        sharedListName: sharedList.name,
        itemType: "movie",
        traktId: movie.ids.trakt,
        itemTitle: movie.title,
    });

    for (const doc of docs) {
        batch.delete(doc.ref);
    }

    await batch.commit();
};

export const joinSharedList = async (sharedList, userId) => {
    const batch = firestore.batch();

    batch.update(firestore.collection("sharedLists").doc(sharedList.id), {
        members: firebase.firestore.FieldValue.arrayUnion(userId),
    });

    const conversationChanges = {};
    conversationChanges[`members.${userId}`] = true;
    conversationChanges[
        `notificationPreferences.${userId}.muteAll`
    ] = firebase.firestore.FieldValue.delete();

    batch.update(
        firestore.collection("conversations").doc(sharedList.conversationId),
        conversationChanges
    );

    batch.set(firestore.collection("conversationMessages").doc(), {
        type: "shared_list_user_joined",
        sender: userId,
        createdOn: firebase.firestore.FieldValue.serverTimestamp(),
        conversationId: sharedList.conversationId,
        sharedListId: sharedList.id,
    });

    await batch.commit();
};

const API_URL = isProductionMode
    ? "https://us-central1-movie-pal.cloudfunctions.net"
    : "https://us-central1-movie-pal-debug.cloudfunctions.net";

const getPremiumStatus = async (userId) => {
    const response = await fetch(
        `${API_URL}/premiumPurchaseDetails?userId=${userId}`
    );

    if (response.status === 404) {
        return {};
    }

    return await response.json();
};

export const usePremium = () => {
    const authState = useAuth();

    const [isPremium, setPremium] = useState(null);

    useEffect(() => {
        const loadPremiumStatus = async () => {
            if (authState) {
                if (authState.isAuthenticated) {
                    try {
                        const status = await getPremiumStatus(
                            authState.user.uid
                        );
                        setPremium(status.isActive === true);
                    } catch (e) {
                        setPremium(false);
                    }
                } else {
                    setPremium(false);
                }
            }
        };

        loadPremiumStatus();
    }, [authState]);

    return isPremium;
};

export const createSharedList = async (name, userId) => {
    const batch = firestore.batch();

    const conversationDoc = firestore.collection("conversations").doc();

    const memberValues = {};
    memberValues[userId] = true;
    batch.set(conversationDoc, {
        members: memberValues,
    });

    batch.set(firestore.collection("sharedLists").doc(), {
        name: name,
        createdOn: firebase.firestore.FieldValue.serverTimestamp(),
        members: [userId],
        admins: [userId],
        conversationId: conversationDoc.id,
    });

    await batch.commit();
};
